import { useState, useEffect, useCallback } from 'react';
import { getSupabase } from '@/lib/supabaseClient.js';
import { useToast } from "@/components/ui/use-toast";

export const settingKeys = [
  'site_title',
  'site_logo_url',
  'contact_email',
  'footer_disclaimer',
  'social_facebook_url',
  'social_twitter_url',
  'social_linkedin_url',
  'social_instagram_url', // Added instagram
  'supported_languages',
  'current_terms_version'
];

const defaultSettings = {
  site_title: 'UrbanMint',
  site_logo_url: '/logo_placeholder.svg', // Default placeholder
  contact_email: 'hello@urban-mint.com',
  footer_disclaimer: 'Financial information is for educational purposes only. Consult a professional for advice.',
  social_facebook_url: '',
  social_twitter_url: '',
  social_linkedin_url: '',
  social_instagram_url: '',
  supported_languages: ['en', 'fr', 'es'],
  current_terms_version: '1.0.0'
};

export const useSiteSettings = () => {
  const supabase = getSupabase();
  const { toast } = useToast();
  const [settings, setSettings] = useState(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    if (!supabase) {
      console.warn("Supabase client not available in useSiteSettings for fetchSettings.");
      setIsLoading(false);
      setSettings(defaultSettings);
      setError("Supabase client not available.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('site_settings')
        .select('key, value');

      if (fetchError && fetchError.code === '42P01') {
         throw new Error("Site settings table not found. Please ensure database migrations ran correctly.");
      } else if (fetchError) {
         throw fetchError;
      }

      const fetchedSettings = data.reduce((acc, setting) => {
        // For all settings, just use the string value if present
        acc[setting.key] = typeof setting.value === 'string' ? setting.value : defaultSettings[setting.key];
        return acc;
      }, {});
      
      const initialSettings = { ...defaultSettings }; // Start with defaults
      settingKeys.forEach(key => {
        if (fetchedSettings[key] !== undefined) {
          initialSettings[key] = fetchedSettings[key];
        }
      });


       if (initialSettings.supported_languages && typeof initialSettings.supported_languages === 'string') {
           try {
               initialSettings.supported_languages = JSON.parse(initialSettings.supported_languages);
           } catch (parseError) {
               console.error("Error parsing supported_languages:", parseError);
               initialSettings.supported_languages = defaultSettings.supported_languages; 
           }
       } else if (!Array.isArray(initialSettings.supported_languages) || initialSettings.supported_languages.length === 0) {
           initialSettings.supported_languages = defaultSettings.supported_languages; 
       }
       
       if (!initialSettings.current_terms_version) {
        initialSettings.current_terms_version = defaultSettings.current_terms_version;
       }
       if (!initialSettings.site_logo_url) {
        initialSettings.site_logo_url = defaultSettings.site_logo_url;
       }


      setSettings(initialSettings);

    } catch (err) {
      console.error("Error fetching site settings:", err);
      setError(err.message || "Could not load site settings.");
       setSettings(defaultSettings);
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSetting = useCallback(async (key, value) => {
    if (!supabase) {
      console.warn("Supabase client not available in useSiteSettings for saveSetting.");
      toast({ title: "Save Error", description: "Supabase client not available.", variant: "destructive" });
      return false;
    }
    if (!settingKeys.includes(key)) {
       console.error(`Attempted to save unknown setting key: ${key}`);
       toast({ title: "Save Error", description: `Invalid setting key: ${key}`, variant: "destructive" });
       return false;
    }

    setIsSaving(true);
    setError(null);
    let valueToSave = value;

    if (key === 'supported_languages') {
        if (!Array.isArray(value)) {
            toast({ title: "Save Error", description: "Supported languages must be an array.", variant: "destructive" });
            setIsSaving(false);
            return false;
        }
         valueToSave = value; 
    }


    try {
      const { error: upsertError } = await supabase
        .from('site_settings')
        .upsert({ key: key, value: { value: valueToSave } }, { onConflict: 'key' }); 

      if (upsertError && upsertError.code === '42P01') {
          throw new Error("Site settings table not found. Cannot save.");
      } else if (upsertError) {
          throw upsertError;
      }

      setSettings(prev => ({ ...prev, [key]: value })); 
      toast({ title: "Setting Saved", description: `${key.replace(/_/g, ' ')} updated successfully.` });
      return true;
    } catch (err) {
      console.error(`Error saving setting ${key}:`, err);
      setError(err.message || `Could not save ${key}.`);
      toast({ title: "Save Failed", description: `Could not save ${key}. ${err.message}`, variant: "destructive" });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [supabase, toast]);

  return { settings, isLoading, isSaving, error, fetchSettings, saveSetting };
};
  